import { api } from '@/lib/api';

export type ScanStatus = 'PENDING' | 'CLEAN' | 'INFECTED' | 'FAILED';

/** A document as GET /api/applications/{id}/files lists it. */
export interface AttachedFile {
  fileId: string;
  requirementId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: ScanStatus;
  uploadedAt: string;
}

interface Slot {
  file: AttachedFile;
  uploadUrl: string;
  headers: Record<string, string>;
  expiresAt: string;
}

/** The limits the server enforces, checked here first so nothing too large is ever sent. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

/**
 * Accepted types, by MIME type and by extension: a phone often reports no type for a
 * HEIC photo, and some systems report Office files as octet-stream.
 */
const TYPES: readonly { contentType: string; extensions: readonly string[] }[] = [
  { contentType: 'application/pdf', extensions: ['pdf'] },
  { contentType: 'image/jpeg', extensions: ['jpg', 'jpeg'] },
  { contentType: 'image/png', extensions: ['png'] },
  { contentType: 'image/heic', extensions: ['heic', 'heif'] },
  { contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extensions: ['docx'] },
  { contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extensions: ['xlsx'] },
];

/** For the file picker's `accept`. */
export const ACCEPT = TYPES.flatMap((t) => [t.contentType, ...t.extensions.map((e) => `.${e}`)]).join(',');

export function contentTypeOf(file: { name: string; type: string }): string | null {
  const byType = TYPES.find((t) => t.contentType === file.type || (file.type === 'image/heif' && t.contentType === 'image/heic'));
  if (byType) return byType.contentType;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return TYPES.find((t) => t.extensions.includes(extension))?.contentType ?? null;
}

/** "184 KB", the prototype's unit; at least 1 so a tiny file never shows as nothing. */
export function kilobytes(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('en-US')} KB`;
}

export function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Why a file will not be sent, before anything is: the same rules the server applies. */
export function refusal(file: { name: string; type: string; size: number }): string | null {
  if (contentTypeOf(file) === null) return 'Files must be PDF, JPG, PNG, HEIC, DOCX or XLSX.';
  if (file.size > MAX_FILE_BYTES) return `Each file may be at most 20 MB; this one is ${megabytes(file.size)}.`;
  if (file.size === 0) return 'This file is empty.';
  return null;
}

const base = (applicationId: string) => `/api/applications/${applicationId}/files`;

export const documents = {
  list: (applicationId: string) => api.get<AttachedFile[]>(base(applicationId)),
  remove: (applicationId: string, fileId: string) => api.delete<void>(`${base(applicationId)}/${fileId}`),

  /**
   * Asks for a slot, sends the bytes straight to storage, then confirms. The bytes
   * never pass through this application's API; only the slot and the confirmation do,
   * through lib/api.ts as every API call must.
   */
  async attach(
    applicationId: string,
    requirementId: string,
    file: File,
    onProgress: (fraction: number) => void,
  ): Promise<AttachedFile> {
    const slot = await api.post<Slot>(base(applicationId), {
      requirementId,
      fileName: file.name,
      contentType: contentTypeOf(file),
      sizeBytes: file.size,
    });
    await put(slot, file, onProgress);
    return api.post<AttachedFile>(`${base(applicationId)}/${slot.file.fileId}/complete`);
  },
};

export class UploadFailed extends Error {}

/**
 * XMLHttpRequest rather than fetch, for one reason: fetch reports no upload progress,
 * and a 20 MB passport scan on a phone takes long enough that a person needs to see it
 * moving.
 */
function put(slot: Slot, file: File, onProgress: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', slot.uploadUrl);
    for (const [name, value] of Object.entries(slot.headers)) request.setRequestHeader(name, value);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new UploadFailed('The file could not be uploaded. Check the connection and attach it again.'));
    request.onerror = () =>
      reject(new UploadFailed('The file could not be uploaded. Check the connection and attach it again.'));
    request.send(file);
  });
}
